
import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/lib/supabase";
import { Profile } from "@/hooks/auth/profile/types";
import { getUserLocationFromIP, saveUserLocationPreference } from "@/utils/geo-location";

export function useUserProfile() {
  const [userData, setUserData] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUserData = useCallback(async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      
      if (user) {
        const { data, error } = await supabase
          .from("profiles")
          .select("*")
          .eq("id", user.id)
          .single();
        
        if (error) throw error;
        
        // Create complete profile data with email from auth
        const profileData = {
          ...data,
          email: user.email
        } as Profile;

        setUserData(profileData);

        // If location data is missing, detect and save it automatically
        if (!profileData.country) {
          try {
            console.log("No location data found, attempting to auto-detect");
            const locationData = await getUserLocationFromIP();
            
            if (locationData.country) {
              console.log(`Auto-detected location: ${locationData.country}`);
              
              // Update the profile with detected location
              const { error: updateError } = await supabase
                .from("profiles")
                .update({
                  country: locationData.country,
                  region: locationData.region || null,
                  city: locationData.city || null,
                  location_updated_at: new Date().toISOString()
                })
                .eq("id", user.id);

              if (updateError) {
                console.error("Error updating location:", updateError);
              } else {
                // Refresh data to get updated profile
                const { data: updatedData } = await supabase
                  .from("profiles")
                  .select("*")
                  .eq("id", user.id)
                  .single();

                // Set the updated profile with email
                setUserData({
                  ...updatedData,
                  email: user.email
                } as Profile);
                
                console.log("Profile updated with auto-detected location");
              }
            }
          } catch (locationError) {
            console.error("Error auto-detecting location:", locationError);
            // Continue without location data - don't block the app
          }
        }
      }
    } catch (err: any) {
      console.error("Error fetching user data:", err);
      setError(err.message || "Failed to load user profile");
    } finally {
      setLoading(false);
    }
  }, []);
  
  // Initial data fetch
  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // Function to refresh user data
  const refreshUserData = useCallback(() => {
    return fetchUserData();
  }, [fetchUserData]);

  // Function to update location data
  const updateLocationData = useCallback(async (country: string, region?: string, city?: string) => {
    if (!userData?.id) return false;
    
    try {
      // Fixed: Now properly handling either 1, 2, or 3 arguments
      const success = await saveUserLocationPreference(userData.id, country, region, city);
      if (success) {
        await fetchUserData();
        return true;
      }
      return false;
    } catch (error) {
      console.error("Error updating location data:", error);
      return false;
    }
  }, [userData, fetchUserData]);

  return { 
    userData, 
    loading, 
    error, 
    refreshUserData,
    updateLocationData
  };
}
